/***
 *
 *   SUPER ADMIN — USERS TAB
 *   Master-detail browser over every account on the platform: search left,
 *   the selected account's members, keys and usage right.
 *
 *   Read-only by design. The only privileged action is revealing a customer's
 *   plaintext API key, which the server audit-logs.
 *
 **********/

import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { ViewContext, Card, Animate, Icon, useToast } from "components/lib";
import { requestServer } from "components/hooks/api";
import * as Tabs from "@radix-ui/react-tabs";
import { Bar } from "react-chartjs-2";
import { format } from "date-fns";

const PAGE_SIZE = 25;

const SCOPE_COLORS = {
  maps: "rgb(91 147 255)",
  autocomplete: "rgb(255 214 107)",
  geocode: "rgb(53 150 14)",
  directions: "rgb(255 99 132)",
  mapMatching: "rgb(54 162 235)",
  optimization: "rgb(255 205 86)",
  matrix: "rgb(75 192 192)",
  isochrone: "rgb(153 102 255)",
  elevation: "rgb(255 159 64)",
  navigation: "rgb(201 203 207)",
  tiles: "rgb(102 187 106)",
  staticImages: "rgb(233 30 99)",
};

const inputClass =
  "w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 transition-colors";

function fmtDate(value) {
  if (!value) return "—";
  const d = new Date(value);
  return isNaN(d) ? "—" : format(d, "MMM dd, yyyy");
}

function fmtNum(n) {
  return (Number(n) || 0).toLocaleString();
}

export function UsersTab() {
  const viewContext = useContext(ViewContext);
  const { toast } = useToast();

  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const [page, setPage] = useState(0);
  const [sort, setSort] = useState("recent"); // "recent" | "usage"
  const [list, setList] = useState({ data: [], total: 0 });
  const [listLoading, setListLoading] = useState(true);

  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [expanded, setExpanded] = useState({}); // account_id -> show members inline

  // typing shouldn't fire a request per keystroke; the search hits four
  // correlated subqueries server-side
  useEffect(() => {
    const id = setTimeout(() => {
      setDebounced(search);
      setPage(0);
    }, 350);
    return () => clearTimeout(id);
  }, [search]);

  const fetchList = useCallback(async () => {
    setListLoading(true);
    try {
      const qs = new URLSearchParams({ limit: PAGE_SIZE, offset: page * PAGE_SIZE });
      if (debounced) qs.set("search", debounced);
      if (sort === "usage") qs.set("sort", "usage");
      const res = await requestServer(`/api/admin/accounts?${qs}`, "GET");
      setList({ data: res?.data || [], total: res?.total || 0 });
    } catch (err) {
      viewContext.handleError(err);
    } finally {
      setListLoading(false);
    }
  }, [debounced, page, sort, viewContext]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const selectAccount = useCallback(
    async (accountId) => {
      setSelectedId(accountId);
      setDetailLoading(true);
      setDetail(null);
      try {
        const res = await requestServer(`/api/admin/accounts/${accountId}`, "GET");
        setDetail(res?.data || null);
      } catch (err) {
        viewContext.handleError(err);
      } finally {
        setDetailLoading(false);
      }
    },
    [viewContext]
  );

  const totalPages = Math.ceil(list.total / PAGE_SIZE) || 1;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)] gap-4">
      {/* ---------------- master: account list ---------------- */}
      <div className="min-w-0">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search account, owner, member email or API key…"
          className={inputClass}
        />

        <div className="flex items-center justify-between mt-2 mb-3">
          <p className="text-xs text-gray-500">
            {listLoading ? "Searching…" : `${fmtNum(list.total)} account${list.total === 1 ? "" : "s"}`}
          </p>

          <div className="flex items-center gap-1 text-xs">
            <span className="text-gray-600">Sort</span>
            {[
              ["recent", "Newest"],
              ["usage", "Most usage (30d)"],
            ].map(([value, label]) => (
              <button
                key={value}
                onClick={() => {
                  setSort(value);
                  setPage(0);
                }}
                className={`px-2 py-0.5 rounded border transition-colors ${
                  sort === value
                    ? "bg-blue-500/20 text-blue-200 border-blue-500/30"
                    : "bg-white/5 text-gray-400 border-white/10 hover:bg-white/10"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        <div className="rounded-lg border border-white/10 overflow-hidden">
          {listLoading && !list.data.length ? (
            <p className="p-4 text-gray-400 text-sm">Loading accounts…</p>
          ) : !list.data.length ? (
            <p className="p-4 text-gray-400 text-sm">No accounts match that search.</p>
          ) : (
            list.data.map((a) => (
              <AccountRow
                key={a.account_id}
                account={a}
                selected={selectedId === a.account_id}
                expanded={!!expanded[a.account_id]}
                onSelect={() => selectAccount(a.account_id)}
                onToggle={() =>
                  setExpanded((prev) => ({ ...prev, [a.account_id]: !prev[a.account_id] }))
                }
                detail={selectedId === a.account_id ? detail : null}
              />
            ))
          )}
        </div>

        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-3 text-sm text-gray-400">
            <button
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
              className="px-3 py-1 bg-white/5 border border-white/10 rounded hover:bg-white/10 disabled:opacity-50"
            >
              Previous
            </button>
            <span>
              Page {page + 1} of {totalPages}
            </span>
            <button
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
              disabled={page >= totalPages - 1}
              className="px-3 py-1 bg-white/5 border border-white/10 rounded hover:bg-white/10 disabled:opacity-50"
            >
              Next
            </button>
          </div>
        )}
      </div>

      {/* ---------------- detail: one account ---------------- */}
      <div className="min-w-0">
        {!selectedId ? (
          <div className="h-full min-h-[20rem] flex items-center justify-center rounded-lg border border-dashed border-white/10">
            <p className="text-gray-500 text-sm">Select an account to see its keys and usage.</p>
          </div>
        ) : detailLoading ? (
          <p className="p-4 text-gray-400 text-sm">Loading account…</p>
        ) : !detail ? (
          <p className="p-4 text-gray-400 text-sm">Account not found.</p>
        ) : (
          <AccountDetail detail={detail} toast={toast} viewContext={viewContext} />
        )}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */

function AccountRow({ account, selected, expanded, onSelect, onToggle, detail }) {
  return (
    <div className={`border-b border-white/5 last:border-b-0 ${selected ? "bg-blue-500/10" : ""}`}>
      <div
        onClick={onSelect}
        className="flex items-center gap-3 p-3 cursor-pointer hover:bg-white/5 transition-colors"
      >
        <div className="min-w-0 flex-1">
          <p className="text-white text-sm font-medium truncate">{account.account_name || "—"}</p>
          <p className="text-gray-400 text-xs truncate">{account.owner_email}</p>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <span
            className="text-right leading-tight"
            title={`${fmtNum(account.requests_30d)} requests in the last 30 days, ${fmtNum(account.total_requests)} all time`}
          >
            <span
              className={`block text-xs tabular-nums ${
                account.requests_30d ? "text-white" : "text-gray-600"
              }`}
            >
              {fmtNum(account.requests_30d)}
            </span>
            <span className="block text-xs tabular-nums text-gray-300">
              {fmtNum(account.total_requests)} all time
            </span>
          </span>
          <span className="text-xs text-gray-500">{account.key_count} keys</span>
          <span
            className={`px-2 py-0.5 rounded-full text-xs font-semibold border ${
              account.plan === "free"
                ? "bg-gray-500/20 text-gray-300 border-gray-500/30"
                : "bg-green-500/20 text-green-300 border-green-500/30"
            }`}
          >
            {account.plan === "free" ? "free" : "paid"}
          </span>
          {account.member_count > 1 && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onToggle();
              }}
              className="text-gray-500 hover:text-white text-xs px-1"
              title="Show members"
            >
              {expanded ? "▾" : "▸"} {account.member_count}
            </button>
          )}
        </div>
      </div>

      {expanded && (
        <div className="px-3 pb-3 pl-6 text-xs text-gray-400 space-y-1">
          {detail?.members?.length ? (
            detail.members.map((m) => (
              <p key={m.id} className="truncate">
                {m.email} <span className="text-gray-600">· {m.permission}</span>
              </p>
            ))
          ) : (
            <p className="text-gray-600">Select the account to load its members.</p>
          )}
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */

function AccountDetail({ detail, toast, viewContext }) {
  const [tab, setTab] = useState("overview");

  return (
    <div className="rounded-lg border border-white/10 p-4">
      <div className="mb-4">
        <h3 className="text-white text-lg font-semibold">{detail.name || "—"}</h3>
        <p className="text-gray-400 text-sm">
          {detail.members?.find((m) => m.permission === "owner")?.email || "no owner"}
          {" · "}
          {detail.plan === "free" ? "free" : detail.plan}
          {" · "}
          {detail.active ? "active" : "inactive"}
        </p>
      </div>

      <Tabs.Root value={tab} onValueChange={setTab}>
        <Tabs.List className="flex flex-wrap gap-2 mb-4 border-b border-white/10 pb-2">
          {[
            ["overview", "Overview"],
            ["keys", `API keys (${detail.keys?.length || 0})`],
            ["usage", "Usage"],
            ["members", `Members (${detail.members?.length || 0})`],
          ].map(([value, label]) => (
            <Tabs.Trigger
              key={value}
              value={value}
              className="px-3 py-1.5 text-sm text-white/60 hover:text-white transition-colors data-[state=active]:text-white data-[state=active]:border-b-2 data-[state=active]:border-blue-500"
            >
              {label}
            </Tabs.Trigger>
          ))}
        </Tabs.List>

        <Tabs.Content value="overview">
          <dl className="grid grid-cols-2 gap-y-3 text-sm">
            <Field label="Account ID" value={detail.id} mono />
            <Field label="Created" value={fmtDate(detail.date_created)} />
            <Field label="Plan" value={detail.plan || "free"} />
            <Field label="Status" value={detail.active ? "Active" : "Inactive"} />
            <Field label="Members" value={detail.members?.length || 0} />
            <Field label="API keys" value={detail.keys?.length || 0} />
            <Field label="Stripe customer" value={detail.stripe_customer_id || "—"} mono />
            <Field label="Stripe subscription" value={detail.stripe_subscription_id || "—"} mono />
          </dl>
        </Tabs.Content>

        <Tabs.Content value="keys">
          <KeysPanel accountId={detail.id} keys={detail.keys || []} toast={toast} viewContext={viewContext} />
        </Tabs.Content>

        <Tabs.Content value="usage">
          <UsagePanel accountId={detail.id} viewContext={viewContext} />
        </Tabs.Content>

        <Tabs.Content value="members">
          {!detail.members?.length ? (
            <p className="text-gray-400 text-sm">No members.</p>
          ) : (
            <div className="space-y-2">
              {detail.members.map((m) => (
                <div
                  key={m.id}
                  className="flex items-center justify-between p-3 rounded-lg bg-white/5 border border-white/10"
                >
                  <div className="min-w-0">
                    <p className="text-white text-sm truncate">{m.name || "—"}</p>
                    <p className="text-gray-400 text-xs truncate">{m.email}</p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0 text-xs">
                    <span className="text-gray-400">{m.permission}</span>
                    {!m.verified && <span className="text-orange-300">unverified</span>}
                    {m.disabled && <span className="text-red-300">disabled</span>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Tabs.Content>
      </Tabs.Root>
    </div>
  );
}

function Field({ label, value, mono }) {
  return (
    <div className="min-w-0">
      <dt className="text-gray-500 text-xs uppercase tracking-wide">{label}</dt>
      <dd className={`text-white truncate ${mono ? "font-mono text-xs" : ""}`}>{value}</dd>
    </div>
  );
}

/* ------------------------------------------------------------------ */

function KeysPanel({ accountId, keys, toast, viewContext }) {
  const [revealed, setRevealed] = useState({}); // key id -> plaintext
  const [busy, setBusy] = useState(null);

  // the server writes an audit row before it hands the key back
  const reveal = async (keyId) => {
    if (revealed[keyId]) return revealed[keyId];
    setBusy(keyId);
    try {
      const res = await requestServer(`/api/admin/accounts/${accountId}/keys/${keyId}/reveal`, "GET");
      const full = res?.data?.key;
      if (!full) throw new Error("No key returned");
      setRevealed((prev) => ({ ...prev, [keyId]: full }));
      return full;
    } catch (err) {
      viewContext.handleError(err);
      return null;
    } finally {
      setBusy(null);
    }
  };

  const copy = async (keyId) => {
    const full = await reveal(keyId);
    if (!full) return;
    try {
      await navigator.clipboard.writeText(full);
      toast({ title: "Key copied", description: "The customer's API key is on your clipboard." });
    } catch {
      toast({ title: "Copy failed", description: "Could not write to the clipboard.", variant: "error" });
    }
  };

  if (!keys.length) return <p className="text-gray-400 text-sm">This account has no API keys.</p>;

  return (
    <div className="space-y-2">
      <p className="text-xs text-gray-500">
        Revealing a key is recorded against your account. Request counts are last 30 days, with the
        all-time total beneath.
      </p>
      {keys.map((k) => (
        <div key={k.id} className="p-3 rounded-lg bg-white/5 border border-white/10">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="text-white text-sm truncate">{k.name || k.id}</p>
              <p className="text-gray-400 text-xs font-mono break-all">
                {revealed[k.id] || k.key_masked}
              </p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <span
                className="text-right leading-tight"
                title={`${fmtNum(k.requests_30d)} requests in the last 30 days, ${fmtNum(k.total_requests)} all time`}
              >
                <span
                  className={`block text-xs tabular-nums ${k.requests_30d ? "text-white" : "text-gray-600"}`}
                >
                  {fmtNum(k.requests_30d)}
                </span>
                <span className="block text-xs tabular-nums text-gray-300">
                  {fmtNum(k.total_requests)} all time
                </span>
              </span>
              {!k.active && <span className="text-xs text-red-300">revoked</span>}
              <button
                onClick={() => reveal(k.id)}
                disabled={busy === k.id || !!revealed[k.id]}
                className="px-2 py-1 text-xs bg-white/5 border border-white/10 rounded hover:bg-white/10 disabled:opacity-50"
              >
                {busy === k.id ? "…" : revealed[k.id] ? "Revealed" : "Reveal"}
              </button>
              <button
                onClick={() => copy(k.id)}
                className="px-2 py-1 text-xs bg-white/5 border border-white/10 rounded hover:bg-white/10"
              >
                Copy
              </button>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-1 mt-2 text-xs">
            <span className="text-gray-500">Sites</span>
            {k.allowed_websites?.length ? (
              k.allowed_websites.map((w) => (
                <span
                  key={w}
                  className="px-2 py-0.5 rounded bg-white/10 border border-white/10 text-gray-200"
                >
                  {w}
                </span>
              ))
            ) : (
              // no entry means the key is accepted from any origin
              <span className="px-2 py-0.5 rounded bg-orange-500/15 border border-orange-500/30 text-orange-200">
                any origin
              </span>
            )}
          </div>

          <div className="flex flex-wrap gap-1 mt-2">
            {(k.scopes || []).map((s) => (
              <span
                key={s}
                className="px-2 py-0.5 rounded text-xs text-white"
                style={{ backgroundColor: SCOPE_COLORS[s] || "rgb(120 120 120)", opacity: 0.65 }}
              >
                {s}
              </span>
            ))}
            <span className="text-xs text-gray-500 ml-auto">{fmtDate(k.date_created)}</span>
          </div>
        </div>
      ))}
    </div>
  );
}

/* ------------------------------------------------------------------ */

function UsagePanel({ accountId, viewContext }) {
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const res = await requestServer(`/api/admin/accounts/${accountId}/usage`, "GET");
        if (!cancelled) setUsage(res?.data || null);
      } catch (err) {
        if (!cancelled) viewContext.handleError(err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [accountId, viewContext]);

  const chart = useMemo(() => {
    if (!usage?.byDay?.length) return null;
    return {
      labels: usage.byDay.map((d) => d.date.slice(5)),
      datasets: [
        {
          label: "Requests",
          data: usage.byDay.map((d) => d.count),
          backgroundColor: "rgba(59,130,246,0.8)",
          borderColor: "rgba(59,130,246,0.8)",
          borderWidth: 2,
          barThickness: 12,
        },
      ],
    };
  }, [usage]);

  if (loading) return <p className="text-gray-400 text-sm">Loading usage…</p>;
  if (!usage) return <p className="text-gray-400 text-sm">No usage available.</p>;

  if (!usage.key_count)
    return <p className="text-gray-400 text-sm">This account has no API keys, so there is no usage.</p>;

  return (
    <div>
      <p className="text-sm text-gray-400 mb-3">
        <span className="text-white font-semibold">{fmtNum(usage.total)}</span> requests ·{" "}
        {usage.startDate} to {usage.endDate}
      </p>

      {chart ? (
        <div className="h-48 mb-4">
          <Bar
            data={chart}
            options={{
              responsive: true,
              maintainAspectRatio: false,
              plugins: { legend: { display: false } },
              scales: {
                x: { ticks: { color: "rgba(255,255,255,0.7)" }, grid: { color: "rgba(255,255,255,0.08)" } },
                y: { ticks: { color: "rgba(255,255,255,0.7)" }, grid: { color: "rgba(255,255,255,0.1)" } },
              },
            }}
          />
        </div>
      ) : (
        <p className="text-gray-500 text-sm mb-4">No requests in this window.</p>
      )}

      {!!usage.byScope?.length && (
        <div className="space-y-1">
          {usage.byScope.map((s) => (
            <div key={s.scope} className="flex items-center justify-between text-sm">
              <span className="flex items-center gap-2 text-gray-300">
                <span
                  className="w-2 h-2 rounded-full inline-block"
                  style={{ backgroundColor: SCOPE_COLORS[s.scope] || "rgb(120 120 120)" }}
                />
                {s.label || s.scope}
              </span>
              <span className="text-white">{fmtNum(s.count)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
